import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Play, 
  ChevronRight, 
  Bookmark,
  Share2,
  MessageSquare,
  Globe,
  Loader2,
  MoreHorizontal,
  Calendar,
  Trash2,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import OfficialDarshanPost from '../components/OfficialDarshanPost';
import DharmaShareCard from '../components/DharmaShareCard';

import { getMediaUrl } from '@/utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POSTS_PER_PAGE = 10;

// Helper function to determine media type from URL if not provided
const getMediaType = (url, type) => {
  if (type) return type;
  if (!url) return null;
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.match(/\.(mp4|webm|mov|avi|mkv)(\?|$)/)) return 'video';
  if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/)) return 'image';
  // Default to image for unsplash and other image CDNs
  if (lowerUrl.includes('unsplash.com') || lowerUrl.includes('images.')) return 'image';
  return 'image'; // Default fallback
};

// Post Media Component
const PostMedia = ({ mediaUrl, mediaType }) => {
  if (!mediaUrl) return null;
  
  const url = getMediaUrl(mediaUrl);
  const type = getMediaType(mediaUrl, mediaType);
  
  if (type === 'video') {
    return (
      <div className="px-4 pb-3">
        <video 
          src={url} 
          controls 
          className="w-full rounded-xl max-h-96"
          preload="metadata"
        />
      </div>
    );
  }
  
  return (
    <div className="px-4 pb-3">
      <img 
        src={url} 
        alt="Post" 
        className="w-full rounded-xl object-cover max-h-80" 
        loading="lazy"
        onError={(e) => {
          console.error('Image failed to load:', url);
          e.target.style.display = 'none';
        }}
      />
    </div>
  );
};

// Generated temple images
const TEMPLE_IMAGES = {
  dailyDharma: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/506c1ce17ccf3e88bdc4500400708a9a9a5773974d57149bcf393d405fead611.png',
  kashi: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/f60696cdd6b05088d0faf888b49e6f1aaee3895096cdce1e651785d44194278a.png',
  jagannath: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/60c5190006a26195453e919f8ecee2dd0c12b7cfe4c0f09a68bb5b9fb5879f03.png',
  dwarka: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/8f61f1582ff5ce1e186deeadad2b05f24d66814196fde1d2c5f082c2dea6b1b1.png',
  tirupati: 'https://static.prod-images.emergentagent.com/jobs/792f5e6c-8f01-4dd7-92b6-68dd9d602feb/images/3afe6d01b91a3743c7cf912ababfd6fbe21bdf5f5ac0fa8baadcfe520368d817.png'
};

// Temple Darshan data
const TEMPLE_DARSHANS = [
  { id: 1, name: 'Kashi Vishwanath', type: 'Live Aarti', isLive: true, image: TEMPLE_IMAGES.kashi },
  { id: 2, name: 'Jagannath Puri', type: 'Morning Darshan', isLive: false, image: TEMPLE_IMAGES.jagannath },
  { id: 3, name: 'Dwarka', type: 'Evening Aarti', isLive: false, image: TEMPLE_IMAGES.dwarka },
  { id: 4, name: 'Tirupati Balaji', type: 'Now Live', isLive: true, image: TEMPLE_IMAGES.tirupati }
];

// Scripture icons
const GitaIcon = () => (
  <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
    <rect x="8" y="4" width="32" height="40" rx="3" fill="#FF9933" opacity="0.2"/>
    <rect x="10" y="6" width="28" height="36" rx="2" fill="#FF9933"/>
    <text x="24" y="28" textAnchor="middle" fill="white" fontSize="10" fontFamily="serif">ॐ</text>
  </svg>
);

const RamayanaIcon = () => (
  <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
    <path d="M24 8L8 24L24 40L40 24L24 8Z" fill="#E91E63" opacity="0.2"/>
    <path d="M24 12L12 24L24 36L36 24L24 12Z" fill="#E91E63"/>
    <path d="M18 24L24 18L30 24" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    <path d="M24 18V32" stroke="white" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const UpanishadIcon = () => (
  <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="16" fill="#FF9800" opacity="0.2"/>
    <circle cx="24" cy="24" r="12" fill="#FF9800"/>
    <path d="M24 16C20 20 20 28 24 32C28 28 28 20 24 16Z" fill="white"/>
  </svg>
);

const ShivPuranIcon = () => (
  <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
    <ellipse cx="24" cy="32" rx="14" ry="8" fill="#795548" opacity="0.3"/>
    <ellipse cx="24" cy="28" rx="12" ry="7" fill="#795548"/>
    <circle cx="24" cy="16" r="8" fill="#8D6E63"/>
  </svg>
);

// Popular Gurus
const POPULAR_GURUS = [
  { name: 'Swami Vivekananda', followers: '1.80K', avatar: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1e/Swami_Vivekananda-1893-09-signed.jpg/220px-Swami_Vivekananda-1893-09-signed.jpg' },
  { name: 'Adi Shankaracharya', followers: '750K', avatar: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/Raja_Ravi_Varma_-_Sankaracharya.jpg/220px-Raja_Ravi_Varma_-_Sankaracharya.jpg' },
  { name: 'Tulsidas', followers: '620K', avatar: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Tulsidas_Jayanti.jpg/220px-Tulsidas_Jayanti.jpg' },
  { name: 'Meera Bai', followers: '620K', avatar: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/52/Mirabai_painting.jpg/220px-Mirabai_painting.jpg' }
];

// Upcoming Festivals
const UPCOMING_FESTIVALS = [
  { name: 'Maha Shivratri', date: 'March 8, 2025', icon: '🔱' },
  { name: 'Ram Navami', date: 'April 17, 2025', icon: '🏹' },
  { name: 'Hanuman Jayanti', date: 'April 23, 2025', icon: '🙏' }
];

export default function DharmBookHomePage({ user }) {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [feedFilter, setFeedFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPosts, setTotalPosts] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [dailyDharma, setDailyDharma] = useState(null);
  const [showShareCard, setShowShareCard] = useState(false);
  
  // Ref for infinite scroll observer
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const desktopFeedRef = useRef(null);

  // Fetch daily dharma quote
  useEffect(() => {
    const fetchDailyDharma = async () => {
      try {
        const res = await axios.get(`${API}/daily-dharma/today`);
        setDailyDharma(res.data);
      } catch (e) {
        console.error('Failed to load daily dharma:', e);
      }
    };
    fetchDailyDharma();
  }, []);

  // Fetch posts with pagination
  const fetchPosts = useCallback(async (pageNum, isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const token = localStorage.getItem('dharmbook_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/posts`, {
        params: { page: pageNum, limit: POSTS_PER_PAGE },
        headers
      });

      const { posts: newPosts, has_more, total } = response.data;

      if (isInitial) {
        setPosts(newPosts || []);
      } else {
        // Append new posts, avoiding duplicates
        setPosts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const uniqueNewPosts = (newPosts || []).filter(p => !existingIds.has(p.id));
          return [...prev, ...uniqueNewPosts];
        });
      }

      setHasMore(has_more);
      setTotalPosts(total);
    } catch (error) {
      console.error('Error fetching posts:', error);
      if (isInitial) {
        setPosts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchPosts(1, true);
  }, [fetchPosts]);

  // Load more posts function
  const loadMorePosts = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage, false);
    }
  }, [page, loadingMore, hasMore, fetchPosts]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (loading) return;

    // Disconnect previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const options = {
      root: null, // Use viewport as root for mobile
      rootMargin: '200px',
      threshold: 0
    };

    const handleObserver = (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore) {
        loadMorePosts();
      }
    };

    observerRef.current = new IntersectionObserver(handleObserver, options);

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadingMore, loadMorePosts]);

  // Desktop scroll handler for infinite scroll
  useEffect(() => {
    const feedContainer = desktopFeedRef.current;
    if (!feedContainer || loading) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = feedContainer;
      // Load more when within 200px of bottom
      if (scrollHeight - scrollTop - clientHeight < 200 && hasMore && !loadingMore) {
        loadMorePosts();
      }
    };

    feedContainer.addEventListener('scroll', handleScroll);
    return () => feedContainer.removeEventListener('scroll', handleScroll);
  }, [loading, hasMore, loadingMore, loadMorePosts]);

  const handleNamaste = async (postId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(prev => prev.map(p => 
        p.id === postId ? { ...p, likes_count: (p.likes_count || 0) + 1, is_liked: true } : p
      ));
    } catch (error) {
      toast.error('Unable to send Namaste');
    }
  };

  // Handle post deletion
  const handleDeleteClick = (post) => {
    setPostToDelete(post);
    setShowDeleteModal(true);
    setOpenMenuId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!postToDelete) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/posts/${postToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove post from state immediately
      setPosts(prev => prev.filter(p => p.id !== postToDelete.id));
      setTotalPosts(prev => prev - 1);
      
      toast.success('Post deleted successfully');
      setShowDeleteModal(false);
      setPostToDelete(null);
    } catch (error) {
      console.error('Delete error:', error);
      if (error.response?.status === 403) {
        toast.error('You can only delete your own posts');
      } else if (error.response?.status === 404) {
        toast.error('Post not found');
      } else {
        toast.error('Failed to delete post');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPostToDelete(null);
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = Math.floor((now - new Date(date)) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hrs ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* ==================== DESKTOP LAYOUT (lg: 1024px+) ==================== */}
      <div className="hidden lg:block">
        <div className="max-w-[1200px] mx-auto px-4 xl:px-6 pt-4">
          <div className="flex gap-4 xl:gap-6" style={{ height: 'calc(100vh - 72px)' }}>
            
            {/* LEFT SIDEBAR - Responsive width */}
            <aside className="w-[220px] xl:w-[250px] flex-shrink-0 overflow-y-auto pb-6 desktop-scrollbar">
              <div className="space-y-4">
                {/* User Card */}
                <div className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <img 
                      src={user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.name || 'User'}`}
                      alt={user?.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-orange-200"
                    />
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{user?.name || 'Guest'}</p>
                      <p className="text-xs text-slate-500">@{user?.username || 'devotee'}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate('/profile')}
                    className="w-full text-center text-sm text-orange-600 font-medium hover:text-orange-700 py-2 border-t border-stone-100"
                  >
                    View Profile
                  </button>
                </div>

                {/* Create Post */}
                <button 
                  onClick={() => navigate('/create-post')}
                  className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors shadow-md text-sm"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                  </svg>
                  Create Post
                </button>

                {/* Scriptures */}
                <div className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm">
                  <h3 className="font-bold text-slate-800 text-sm mb-3">Scriptures</h3>
                  <div className="space-y-1">
                    {[
                      { name: 'Bhagavad Gita', Icon: GitaIcon, path: '/ai-granth/book/bhagavad-gita' },
                      { name: 'Ramayana', Icon: RamayanaIcon, path: '/ai-granth/book/sundarkand' },
                      { name: 'Upanishads', Icon: UpanishadIcon, path: '/ai-granth/book/isha-upanishad' },
                      { name: 'Shiv Puran', Icon: ShivPuranIcon, path: '/ai-granth/book/shiva-purana' },
                    ].map((item) => (
                      <Link key={item.name} to={item.path} className="flex items-center gap-3 p-2 hover:bg-orange-50 rounded-xl transition-colors group">
                        <item.Icon />
                        <span className="font-medium text-slate-700 text-sm group-hover:text-orange-600">{item.name}</span>
                      </Link>
                    ))}
                  </div>
                  <Link to="/ai-granth" className="block text-center text-sm text-orange-600 font-medium mt-3 pt-3 border-t border-stone-100 hover:text-orange-700">
                    View All Scriptures
                  </Link>
                </div>

                {/* Live Darshan */}
                <div className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm">
                  <h3 className="font-bold text-slate-800 text-sm mb-3">Live Darshan</h3>
                  <div className="space-y-2">
                    {TEMPLE_DARSHANS.slice(0, 3).map((temple) => (
                      <Link key={temple.id} to="/live" className="flex items-center gap-3 p-2 hover:bg-orange-50 rounded-xl transition-colors group">
                        <img src={temple.image} alt={temple.name} className="w-10 h-10 rounded-lg object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-700 text-sm group-hover:text-orange-600 truncate">{temple.name}</p>
                          <p className="text-xs text-slate-500">{temple.type}</p>
                        </div>
                        {temple.isLive && <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded">LIVE</span>}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Saved */}
                <Link to="/saved" className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-stone-100 shadow-sm hover:border-orange-200 transition-colors">
                  <Bookmark className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-slate-700 text-sm">Saved Posts</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                </Link>
              </div>
            </aside>

            {/* CENTER FEED - Flexible width */}
            <main ref={desktopFeedRef} className="flex-1 min-w-0 overflow-y-auto pb-6 desktop-scrollbar">
              {/* Hero Banner */}
              <div className="relative rounded-2xl overflow-hidden mb-6" style={{ height: '180px' }}>
                <img src={TEMPLE_IMAGES.dailyDharma} alt="Temple" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
                <div className="relative p-6 flex flex-col justify-center h-full">
                  <p className="text-xs text-orange-300 font-semibold mb-1 tracking-wider uppercase">Daily Dharma</p>
                  <p className="text-2xl text-white font-bold mb-1" style={{ fontFamily: 'Noto Serif Devanagari, serif' }}>
                    " {dailyDharma?.sanskrit || 'धर्मो रक्षति रक्षितः'} "
                  </p>
                  <p className="text-orange-100 text-sm mb-3">{dailyDharma?.meaning || 'Dharma Protects Those Who Protect It'}</p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => dailyDharma?.youtube_url && window.open(dailyDharma.youtube_url, '_blank')}
                      className="px-4 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-semibold text-sm flex items-center gap-2 shadow-lg transition-colors"
                      data-testid="desktop-listen-btn"
                    >
                      <Play className="w-3 h-3 fill-white" /> Listen
                    </button>
                    <button
                      onClick={() => navigate('/daily-dharma')}
                      className="text-white text-sm font-medium flex items-center gap-1 hover:text-orange-200 transition-colors"
                      data-testid="desktop-read-full-btn"
                    >
                      Read Full <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setShowShareCard(true)}
                      className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors ml-1"
                      data-testid="desktop-share-dharma-btn"
                      title="Share Daily Dharma"
                    >
                      <Share2 className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Create Post Box */}
              <div className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <img 
                    src={user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.name || 'User'}`}
                    alt={user?.name}
                    className="w-10 h-10 rounded-full object-cover border-2 border-orange-200 flex-shrink-0"
                  />
                  <button 
                    onClick={() => navigate('/create-post')}
                    className="flex-1 text-left px-4 py-2.5 bg-stone-50 rounded-full text-slate-500 text-sm hover:bg-stone-100 transition-colors border border-stone-200 whitespace-nowrap overflow-hidden text-ellipsis"
                  >
                    What wisdom are you reflecting today?
                  </button>
                </div>
                <div className="flex items-center justify-around border-t border-stone-100 pt-3">
                  <button onClick={() => navigate('/create-post?type=text')} className="flex items-center gap-2 px-3 py-1.5 hover:bg-orange-50 rounded-lg transition-colors active:scale-95">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7V4h16v3"/><path d="M9 20h6"/><path d="M12 4v16"/></svg>
                    <span className="font-medium text-slate-600 text-sm">Text</span>
                  </button>
                  <button onClick={() => navigate('/create-post?type=photo')} className="flex items-center gap-2 px-3 py-1.5 hover:bg-emerald-50 rounded-lg transition-colors active:scale-95">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" fill="#16A34A" /><polyline points="21,15 16,10 5,21" /></svg>
                    <span className="font-medium text-slate-600 text-sm">Photo</span>
                  </button>
                  <button onClick={() => navigate('/create-post?type=video')} className="flex items-center gap-2 px-3 py-1.5 hover:bg-red-50 rounded-lg transition-colors active:scale-95">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2"><rect x="2" y="4" width="14" height="16" rx="2" /><path d="M16 10l6-3v10l-6-3v-4z" fill="#DC2626" /></svg>
                    <span className="font-medium text-slate-600 text-sm">Video</span>
                  </button>
                  <button onClick={() => navigate('/create-post?type=feeling')} className="flex items-center gap-2 px-3 py-1.5 hover:bg-amber-50 rounded-lg transition-colors active:scale-95">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><circle cx="9" cy="9" r="1" fill="#EA580C"/><circle cx="15" cy="9" r="1" fill="#EA580C"/></svg>
                    <span className="font-medium text-slate-600 text-sm">Feeling</span>
                  </button>
                </div>
              </div>

              {/* Today's Darshan */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-slate-800">Today's Darshan</h2>
                  <Link to="/live" className="text-orange-600 font-medium text-sm flex items-center gap-1 hover:text-orange-700">
                    See All <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {TEMPLE_DARSHANS.map((temple) => (
                    <Link key={temple.id} to="/live" className="relative rounded-xl overflow-hidden h-24 group">
                      <img src={temple.image} alt={temple.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                      {temple.isLive && (
                        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-red-500 rounded text-[9px] text-white font-bold flex items-center gap-1">
                          <span className="w-1 h-1 bg-white rounded-full animate-pulse" /> Live
                        </div>
                      )}
                      <div className="absolute bottom-1.5 left-1.5 right-1.5">
                        <p className="text-white font-semibold text-xs truncate">{temple.name}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Feed Section */}
              <div>
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-stone-200">
                  <h2 className="text-lg font-bold text-slate-800">Feed</h2>
                  <div className="flex items-center gap-2">
                    {['All', 'Scriptures', 'Bhakti', 'Stories'].map((filter) => (
                      <button
                        key={filter}
                        onClick={() => setFeedFilter(filter.toLowerCase())}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          feedFilter === filter.toLowerCase() ? 'bg-orange-500 text-white' : 'bg-stone-100 text-slate-600 hover:bg-stone-200'
                        }`}
                      >
                        {filter}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Posts */}
                <div className="space-y-6">
                  {posts.length === 0 && !loading ? (
                    <div className="bg-white rounded-2xl border border-stone-100 p-8 text-center shadow-sm">
                      <Globe className="w-10 h-10 text-orange-300 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium mb-1">No posts yet</p>
                      <p className="text-sm text-slate-500">Be the first to share your wisdom!</p>
                    </div>
                  ) : (
                    <>
                      {posts.map((post) => (
                        post.type === 'daily_darshan' ? (
                          <OfficialDarshanPost key={post.id} post={post} onNamaste={handleNamaste} />
                        ) : (
                        <article key={post.id} className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between p-4 pb-3">
                            <Link to={`/profile/${post.user_id}`} className="flex items-center gap-3">
                              <img 
                                src={post.user_avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${post.user_name}`} 
                                alt={post.user_name}
                                className="w-10 h-10 rounded-full object-cover border-2 border-stone-200"
                              />
                              <div>
                                <p className="font-semibold text-slate-800 text-sm flex items-center gap-1">
                                  {post.user_name || 'Devotee'}
                                  {post.is_official && <span className="text-orange-500 text-xs">✓</span>}
                                </p>
                                <p className="text-xs text-slate-500">{formatTimeAgo(post.created_at)}</p>
                              </div>
                            </Link>
                            <div className="relative">
                              <button 
                                onClick={() => setOpenMenuId(openMenuId === post.id ? null : post.id)}
                                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-stone-100 rounded-lg"
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </button>
                              {openMenuId === post.id && (
                                <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border border-stone-200 py-1 z-20 min-w-[140px]">
                                  {user?.id === post.user_id && (
                                    <button 
                                      onClick={() => handleDeleteClick(post)}
                                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Delete Post
                                    </button>
                                  )}
                                  <button 
                                    onClick={() => setOpenMenuId(null)}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-stone-50"
                                  >
                                    <Bookmark className="w-4 h-4" />
                                    Save Post
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="px-4 pb-3">
                            <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                              {post.content?.slice(0, 400)}{post.content?.length > 400 ? '...' : ''}
                            </p>
                          </div>
                          <PostMedia mediaUrl={post.media_url} mediaType={post.media_type} />
                          <div className="flex items-center border-t border-stone-100">
                            <button onClick={() => handleNamaste(post.id)} className={`flex-1 flex items-center justify-center gap-2 py-2.5 transition-colors ${post.is_liked ? 'text-orange-600 bg-orange-50' : 'text-slate-600 hover:bg-orange-50 hover:text-orange-600'}`}>
                              <span className="text-base">🙏</span>
                              <span className="font-medium text-sm">Namaste</span>
                            </button>
                            <div className="w-px h-6 bg-stone-100" />
                            <button className="flex-1 flex items-center justify-center gap-2 py-2.5 text-slate-600 hover:bg-stone-50">
                              <MessageSquare className="w-4 h-4" />
                              <span className="font-medium text-sm">Reflect</span>
                            </button>
                            <div className="w-px h-6 bg-stone-100" />
                            <button className="flex-1 flex items-center justify-center gap-2 py-2.5 text-slate-600 hover:bg-stone-50">
                              <Share2 className="w-4 h-4" />
                              <span className="font-medium text-sm">Share</span>
                            </button>
                            <div className="w-px h-6 bg-stone-100" />
                            <button className="px-4 py-2.5 text-slate-400 hover:text-orange-600">
                              <Bookmark className="w-4 h-4" />
                            </button>
                          </div>
                        </article>
                        )
                      ))}
                      
                      {/* Infinite Scroll Loader */}
                      <div ref={loadMoreRef} className="py-4 flex justify-center">
                        {loadingMore && (
                          <div className="flex items-center gap-2 text-orange-500">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="text-sm font-medium">Loading more posts...</span>
                          </div>
                        )}
                        {!hasMore && posts.length > 0 && (
                          <p className="text-slate-400 text-sm">You've reached the end • {totalPosts} posts total</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </main>

            {/* RIGHT SIDEBAR - Responsive width */}
            <aside className="w-[240px] xl:w-[280px] flex-shrink-0 overflow-y-auto pb-6 desktop-scrollbar">
              <div className="space-y-4">
                {/* Trending Scriptures */}
                <div className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm">
                  <h3 className="font-bold text-orange-600 text-sm mb-3">Trending Scriptures</h3>
                  <div className="space-y-3">
                    {[
                      { name: 'Bhagavad Gita', subtitle: 'Daily Verse', image: TEMPLE_IMAGES.kashi, path: '/ai-granth/book/bhagavad-gita' },
                      { name: 'Ramayana', subtitle: 'Katha', image: TEMPLE_IMAGES.jagannath, path: '/ai-granth/book/sundarkand' },
                      { name: 'Upanishads', subtitle: 'Wisdom', image: TEMPLE_IMAGES.dwarka, path: '/ai-granth/book/isha-upanishad' },
                    ].map((item) => (
                      <Link key={item.name} to={item.path} className="flex items-center gap-3 p-2 hover:bg-orange-50 rounded-xl transition-colors group">
                        <img src={item.image} alt={item.name} className="w-10 h-10 rounded-lg object-cover" />
                        <div>
                          <p className="font-medium text-slate-800 text-sm group-hover:text-orange-600">{item.name}</p>
                          <p className="text-xs text-slate-500">{item.subtitle}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Popular Gurus */}
                <div className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm">
                  <h3 className="font-bold text-orange-600 text-sm mb-3">Popular Gurus</h3>
                  <div className="space-y-3">
                    {POPULAR_GURUS.map((guru) => (
                      <div key={guru.name} className="flex items-center gap-3 p-2 hover:bg-orange-50 rounded-xl transition-colors cursor-pointer">
                        <img 
                          src={guru.avatar} 
                          alt={guru.name} 
                          className="w-9 h-9 rounded-full object-cover"
                          onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${guru.name}`; }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 text-sm truncate">{guru.name}</p>
                          <p className="text-xs text-slate-500">{guru.followers} Followers</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Upcoming Festivals */}
                <div className="bg-white rounded-2xl p-4 border border-stone-100 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-orange-600 text-sm">Upcoming Festivals</h3>
                    <Calendar className="w-4 h-4 text-orange-400" />
                  </div>
                  <div className="space-y-3">
                    {UPCOMING_FESTIVALS.map((festival) => (
                      <div key={festival.name} className="flex items-center gap-3 p-2 hover:bg-orange-50 rounded-xl transition-colors cursor-pointer">
                        <span className="text-xl">{festival.icon}</span>
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{festival.name}</p>
                          <p className="text-xs text-slate-500">{festival.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Daily Wisdom */}
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-4 border border-orange-100">
                  <p className="text-orange-600 font-semibold text-xs mb-2">Today's Wisdom</p>
                  <p className="text-amber-900 font-bold text-base mb-1" style={{ fontFamily: 'Noto Serif Devanagari, serif' }}>
                    " धर्मो रक्षति रक्षितः "
                  </p>
                  <p className="text-amber-700 text-xs italic">Dharma protects those who protect it</p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* ==================== MOBILE/TABLET LAYOUT (below 1024px) ==================== */}
      <div className="lg:hidden">
        {/* Mobile Hero */}
        <section className="relative">
          <div className="relative mx-3 mt-3 rounded-2xl overflow-hidden" style={{ minHeight: '220px' }}>
            <div className="absolute inset-0">
              <img src={TEMPLE_IMAGES.dailyDharma} alt="Temple" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-orange-50/95 via-orange-50/80 to-transparent" />
            </div>
            <div className="relative p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-orange-600 text-xl font-bold" style={{ fontFamily: 'Noto Serif Devanagari, serif' }}>ॐ</span>
                <span className="text-orange-700 font-semibold text-sm tracking-wide">Daily Dharma</span>
              </div>
              <p className="text-2xl md:text-3xl text-orange-900 font-bold mb-1 leading-tight" style={{ fontFamily: 'Noto Serif Devanagari, serif' }}>
                " {dailyDharma?.sanskrit || 'धर्मो रक्षति रक्षितः'} "
              </p>
              <p className="text-orange-800 text-sm md:text-base mb-5 italic">{dailyDharma?.meaning || 'Dharma Protects Those Who Protect It'}</p>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => dailyDharma?.youtube_url && window.open(dailyDharma.youtube_url, '_blank')}
                  className="px-5 py-2.5 bg-orange-500 text-white rounded-full font-semibold text-sm flex items-center gap-2 hover:bg-orange-600 shadow-lg transition-colors"
                  data-testid="mobile-listen-btn"
                >
                  <Play className="w-4 h-4 fill-white" /> Listen
                </button>
                <button
                  onClick={() => navigate('/daily-dharma')}
                  className="text-orange-800 font-semibold text-sm flex items-center gap-1"
                  data-testid="mobile-read-full-btn"
                >
                  Read Full <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowShareCard(true)}
                  className="w-9 h-9 rounded-full bg-orange-500/20 hover:bg-orange-500/30 flex items-center justify-center transition-colors"
                  data-testid="mobile-share-dharma-btn"
                  title="Share"
                >
                  <Share2 className="w-4 h-4 text-orange-700" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile Darshan */}
        <section className="mt-5 px-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-800">Today's Darshan</h2>
            <Link to="/live" className="text-orange-600 text-sm font-semibold flex items-center gap-1">
              See All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {TEMPLE_DARSHANS.map((temple) => (
              <Link key={temple.id} to="/live" className="flex-shrink-0 w-40 group">
                <div className="relative rounded-2xl overflow-hidden h-28 shadow-md">
                  <img src={temple.image} alt={temple.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  {temple.isLive && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-red-500 rounded-lg text-[10px] text-white font-bold flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> Live
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-white font-bold text-sm truncate">{temple.name}</p>
                    <p className="text-orange-200 text-[11px]">{temple.type}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Mobile Create Post */}
        <section className="mt-5 mx-3">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-orange-400 flex-shrink-0">
                <img src={user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.name || 'User'}`} alt={user?.name} className="w-full h-full object-cover" />
              </div>
              <button onClick={() => navigate('/create-post')} className="flex-1 text-left px-4 py-3 bg-stone-50 rounded-full text-slate-500 text-sm hover:bg-stone-100 border border-stone-200 whitespace-nowrap overflow-hidden text-ellipsis">
                What wisdom are you reflecting today?
              </button>
            </div>
            <div className="flex items-center justify-between border-t border-stone-100 pt-3">
              <button onClick={() => navigate('/create-post?type=text')} className="flex items-center gap-2 px-3 py-2 hover:bg-orange-50 rounded-lg transition-colors active:scale-95">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7V4h16v3"/><path d="M9 20h6"/><path d="M12 4v16"/></svg>
                <span className="text-sm font-semibold text-orange-600">Text</span>
              </button>
              <div className="w-px h-6 bg-stone-200" />
              <button onClick={() => navigate('/create-post?type=photo')} className="flex items-center gap-2 px-3 py-2 hover:bg-emerald-50 rounded-lg transition-colors active:scale-95">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" fill="#16A34A" /><polyline points="21,15 16,10 5,21" /></svg>
                <span className="text-sm font-semibold text-emerald-600">Photo</span>
              </button>
              <div className="w-px h-6 bg-stone-200" />
              <button onClick={() => navigate('/create-post?type=video')} className="flex items-center gap-2 px-3 py-2 hover:bg-red-50 rounded-lg transition-colors active:scale-95">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2"><rect x="2" y="4" width="14" height="16" rx="2" /><path d="M16 10l6-3v10l-6-3v-4z" fill="#DC2626" /></svg>
                <span className="text-sm font-semibold text-red-600">Video</span>
              </button>
              <div className="w-px h-6 bg-stone-200" />
              <button onClick={() => navigate('/create-post?type=feeling')} className="flex items-center gap-2 px-3 py-2 hover:bg-amber-50 rounded-lg transition-colors active:scale-95">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><circle cx="9" cy="9" r="1" fill="#EA580C"/><circle cx="15" cy="9" r="1" fill="#EA580C"/></svg>
                <span className="text-sm font-semibold text-orange-600">Feeling</span>
              </button>
            </div>
          </div>
        </section>

        {/* Mobile Scriptures */}
        <section className="mt-5 px-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-800">From the Scriptures</h2>
            <Link to="/ai-granth" className="text-orange-600 text-sm font-semibold flex items-center gap-1">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[
              { name: 'Gita', desc: 'Daily Verse', Icon: GitaIcon, color: 'bg-orange-50', slug: 'bhagavad-gita' },
              { name: 'Ramayana', desc: 'Katha', Icon: RamayanaIcon, color: 'bg-rose-50', slug: 'sundarkand' },
              { name: 'Upanishad', desc: 'Wisdom', Icon: UpanishadIcon, color: 'bg-amber-50', slug: 'isha-upanishad' },
              { name: 'Shiv Puran', desc: 'Stories', Icon: ShivPuranIcon, color: 'bg-stone-100', slug: 'shiva-purana' },
            ].map((item) => (
              <Link key={item.name} to={`/ai-granth/book/${item.slug}`} className={`${item.color} rounded-2xl p-3 text-center hover:shadow-lg transition-all group border border-stone-100`}>
                <div className="flex justify-center mb-1 group-hover:scale-110 transition-transform"><item.Icon /></div>
                <p className="text-sm font-bold text-slate-800">{item.name}</p>
                <p className="text-[10px] text-slate-500 font-medium">{item.desc}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Mobile Feed */}
        <section className="mt-5 px-3 pb-24">
          {posts.length === 0 && !loading ? (
            <div className="bg-white rounded-2xl border border-stone-100 p-8 text-center shadow-sm">
              <Globe className="w-12 h-12 text-orange-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium mb-2">No posts yet</p>
              <p className="text-sm text-slate-500">Be the first to share your wisdom!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                post.type === 'daily_darshan' ? (
                  <OfficialDarshanPost key={post.id} post={post} onNamaste={handleNamaste} isMobile />
                ) : (
                <article key={post.id} className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between p-4 pb-2">
                    <Link to={`/profile/${post.user_id}`} className="flex items-center gap-3">
                      <img src={post.user_avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${post.user_name}`} alt={post.user_name} className="w-11 h-11 rounded-full object-cover border-2 border-stone-200" />
                      <div>
                        <p className="font-bold text-slate-800 flex items-center gap-1">{post.user_name || 'Devotee'}{post.is_official && <span className="text-orange-500">✓</span>}</p>
                        <p className="text-xs text-slate-500">{formatTimeAgo(post.created_at)} • <Globe className="w-3 h-3 inline" /></p>
                      </div>
                    </Link>
                    <div className="relative">
                      <button 
                        onClick={() => setOpenMenuId(openMenuId === `mobile-${post.id}` ? null : `mobile-${post.id}`)}
                        className="p-2 text-slate-400 hover:text-slate-600"
                      >
                        <MoreHorizontal className="w-5 h-5" />
                      </button>
                      {openMenuId === `mobile-${post.id}` && (
                        <div className="absolute right-0 top-10 bg-white rounded-lg shadow-lg border border-stone-200 py-1 z-20 min-w-[140px]">
                          {user?.id === post.user_id && (
                            <button 
                              onClick={() => handleDeleteClick(post)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete Post
                            </button>
                          )}
                          <button 
                            onClick={() => setOpenMenuId(null)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:bg-stone-50"
                          >
                            <Bookmark className="w-4 h-4" />
                            Save Post
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="px-4 pb-3">
                    <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{post.content?.slice(0, 300)}{post.content?.length > 300 ? '...' : ''}</p>
                  </div>
                  <PostMedia mediaUrl={post.media_url} mediaType={post.media_type} />
                  <div className="flex items-center border-t border-stone-100">
                    <button onClick={() => handleNamaste(post.id)} className={`flex-1 flex items-center justify-center gap-2 py-3 ${post.is_liked ? 'text-orange-600 bg-orange-50' : 'text-slate-600 hover:bg-orange-50'}`}>
                      <span className="text-lg">🙏</span><span className="text-sm font-semibold">Namaste</span>
                    </button>
                    <div className="w-px h-8 bg-stone-100" />
                    <button className="flex-1 flex items-center justify-center gap-2 py-3 text-slate-600 hover:bg-stone-50">
                      <Share2 className="w-4 h-4" /><span className="text-sm font-semibold">Share</span>
                    </button>
                    <div className="w-px h-8 bg-stone-100" />
                    <button className="flex-1 flex items-center justify-center gap-2 py-3 text-slate-600 hover:bg-stone-50">
                      <MessageSquare className="w-4 h-4" /><span className="text-sm font-semibold">Reflect</span>
                    </button>
                    <button className="px-4 py-3 text-slate-400 hover:text-orange-600"><Bookmark className="w-5 h-5" /></button>
                  </div>
                </article>
                )
              ))}
              
              {/* Mobile Infinite Scroll Loader */}
              <div ref={loadMoreRef} className="py-6 flex justify-center">
                {loadingMore && (
                  <div className="flex items-center gap-2 text-orange-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm font-medium">Loading more...</span>
                  </div>
                )}
                {!hasMore && posts.length > 0 && (
                  <p className="text-slate-400 text-sm text-center">You've reached the end<br/>{totalPosts} posts total</p>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Delete Post?</h3>
              <button onClick={handleDeleteCancel} className="p-1 hover:bg-stone-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <p className="text-slate-600 text-sm mb-6">
              Are you sure you want to delete this post? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteCancel}
                className="flex-1 py-2.5 px-4 rounded-xl border border-stone-200 text-slate-700 font-medium hover:bg-stone-50 transition-colors"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 py-2.5 px-4 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Daily Dharma Modal */}
      {showShareCard && dailyDharma && (
        <DharmaShareCard quote={dailyDharma} onClose={() => setShowShareCard(false)} />
      )}
    </div>
  );
}
